import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, storeAuthData, setupTokenRefreshTimer } from '../../utils/api';
import ErrorModal from '../modals/ErrorModal';

const AdminLogin = ({ onLoginSuccess }) => {
  const [loginData, setLoginData] = useState({ username: '', password: '', organisation_id: '' });
  const [orgIdInput, setOrgIdInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [mobileAuthError, setMobileAuthError] = useState('');
  
  // Email verification state
  const [emailVerificationError, setEmailVerificationError] = useState({
    show: false,
    email: '',
    userId: '',
    userType: '',
    canResend: false
  });
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for mobile auth errors in URL
  useEffect(() => {
    const error = searchParams.get('error');
    if (error === 'invalid_token') {
      setMobileAuthError('Your session link is invalid. Please log in manually.');
    } else if (error === 'token_expired') {
      setMobileAuthError('Your session has expired. Please log in again.');
    } else if (error === 'exchange_failed') {
      setMobileAuthError('Unable to authenticate from mobile app. Please log in manually.');
    } else if (error === 'token_used') {
      setMobileAuthError('This link has already been used. Please try again from the mobile app.');
    } else if (error === 'rate_limited') {
      setMobileAuthError('Too many authentication attempts. Please wait a moment and try again.');
    }
  }, [searchParams]);

  // Format organisation ID with automatic hyphens
  const formatOrgId = (value) => {
    const cleaned = value.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    const limited = cleaned.slice(0, 14);
    let formatted = '';
    for (let i = 0; i < limited.length; i++) {
      if (i === 4 || i === 6 || i === 8) {
        formatted += '-';
      }
      formatted += limited[i];
    }
    return formatted;
  };

  const handleOrgIdChange = (e) => {
    const value = e.target.value;
    const formatted = formatOrgId(value);
    setOrgIdInput(formatted);
    const fullOrgId = formatted ? `SO-ORG-${formatted}` : '';
    setLoginData({...loginData, organisation_id: fullOrgId});
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, loginData);
      
      // Store all authentication data including refresh token
      storeAuthData(response.data);
      localStorage.setItem('userType', 'admin');
      localStorage.setItem('signInTime', new Date().toISOString());
      
      // Set up proactive token refresh
      setupTokenRefreshTimer();
      
      console.log('✅ Admin login successful, tokens set:', {
        hasToken: !!localStorage.getItem('token'),
        hasRefreshToken: !!localStorage.getItem('refreshToken'),
        userType: localStorage.getItem('userType')
      });
      
      if (onLoginSuccess) {
        onLoginSuccess();
      }
      
      setTimeout(() => {
        navigate('/admin/dashboard');
      }, 100);
      
    } catch (error) {
      console.error('Login error:', error);
      
      const errorDetail = error.response?.data?.detail;
      if (errorDetail && typeof errorDetail === 'object' && errorDetail.error_code === 'EMAIL_NOT_VERIFIED') {
        setEmailVerificationError({
          show: true,
          email: errorDetail.email || '',
          userId: errorDetail.user_id || '',
          userType: errorDetail.user_type || 'admin',
          canResend: errorDetail.can_resend || false
        });
        setResendSuccess(false);
        setResendError('');
      } else {
        const message = typeof errorDetail === 'string' 
          ? errorDetail 
          : (errorDetail?.message || 'Unknown error');
        setErrorModal({
          show: true,
          message: 'Admin login failed: ' + message
        });
      }
    }
    setIsLoading(false);
  };

  const handleResendVerificationEmail = async () => {
    setIsResendingEmail(true);
    setResendError('');
    setResendSuccess(false);
    
    try {
      const response = await axios.post(`${API}/auth/resend-verification-email`, {
        user_id: emailVerificationError.userId,
        user_type: emailVerificationError.userType
      });
      
      if (response.data.success) {
        setResendSuccess(true);
        setResendError('');
      } else {
        setResendError(response.data.message || 'Failed to send verification email');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.detail || 
                          'Failed to send verification email. Please try again.';
      setResendError(errorMessage);
    }
    
    setIsResendingEmail(false);
  };

  const closeEmailVerificationError = () => {
    setEmailVerificationError({ show: false, email: '', userId: '', userType: '', canResend: false });
    setResendSuccess(false);
    setResendError('');
  };

  return (
    <div className="min-h-screen bg-[#0f0f1a] flex flex-col">
      {/* Header */}
      <header className="p-4 flex justify-between items-center">
        <Link to="/" className="flex items-center space-x-2">
          <span className="text-2xl font-bold">
            <span className="text-white">Site</span>
            <span className="text-purple-400">Orbit</span>
          </span>
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          {/* Back to Home */}
          <div className="mb-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center text-gray-300 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Home
            </button>
          </div>

          {/* Login Card */}
          <div className="bg-[#1a1a2e] rounded-2xl p-8 shadow-xl border border-purple-500/20">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-white">Admin Portal</h1>
              <p className="text-gray-400 mt-2">Manage teams and site operations</p>
            </div>

            {/* Mobile Auth Error Message */}
            {mobileAuthError && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-4">
                <p className="text-amber-400 text-sm">{mobileAuthError}</p>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Organisation ID
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <span className="text-gray-500 font-mono text-sm">SO-ORG-</span>
                  </div>
                  <input
                    type="text"
                    value={orgIdInput}
                    onChange={handleOrgIdChange}
                    className="w-full pl-24 pr-4 py-3 bg-[#252540] border border-purple-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors font-mono text-sm"
                    placeholder="XXXX-YY-MM-XXXX"
                    maxLength={17}
                    required
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Enter your organisation ID (hyphens added automatically)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  name="username"
                  value={loginData.username}
                  onChange={(e) => setLoginData({...loginData, username: e.target.value})}
                  className="w-full px-4 py-3 bg-[#252540] border border-purple-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                  placeholder="Enter your username"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                    className="w-full px-4 py-3 bg-[#252540] border border-purple-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors pr-12"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* DEV: Auto-fill button - only shown in development */}
              {process.env.NODE_ENV === 'development' && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setOrgIdInput('STUD-26-01-PSWL');
                      setLoginData({
                        organisation_id: 'SO-ORG-STUD-26-01-PSWL',
                        username: 't.phillip',
                        password: '141086Ojay!!'
                      });
                    }}
                    className="flex-1 bg-yellow-600 text-white font-medium py-2 px-4 rounded-xl hover:bg-yellow-700 transition-all text-sm"
                  >
                    DEV: Studio G4
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setOrgIdInput('G4DE-23-01-LFQO');
                      setLoginData({
                        organisation_id: 'SO-ORG-G4DE-23-01-LFQO',
                        username: 'toyin',
                        password: 'Sunlight2010!'
                      });
                    }}
                    className="flex-1 bg-yellow-600 text-white font-medium py-2 px-4 rounded-xl hover:bg-yellow-700 transition-all text-sm"
                  >
                    DEV: G4 Design
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-4 bg-gradient-to-r from-purple-500 to-purple-700 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-purple-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-purple-500/25"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>
              
              <div className="text-center mt-4">
                <Link
                  to="/forgot-password"
                  className="text-purple-400 hover:text-purple-300 text-sm font-medium transition-colors"
                >
                  Forgot your password?
                </Link>
              </div>
            </form>
          </div>

          {/* Help Text */}
          <p className="text-center text-gray-500 text-sm mt-6">
            Need help? Contact your organisation administrator
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="p-4 text-center text-gray-600 text-sm">
        © {new Date().getFullYear()} SiteOrbit. All rights reserved.
      </footer>

      <ErrorModal
        isOpen={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: '' })}
      />

      {/* Email Verification Required Modal */}
      {emailVerificationError.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-[#1a1a2e] rounded-2xl shadow-2xl border border-purple-500/20 max-w-md w-full overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-4">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Email Verification Required</h3>
                  <p className="text-amber-100 text-sm">Please verify your email to continue</p>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="px-6 py-5">
              <p className="text-gray-300 mb-4">
                Your email address needs to be verified before you can log in.
              </p>
              
              {emailVerificationError.email && (
                <div className="bg-[#252540] rounded-xl p-3 mb-4">
                  <p className="text-sm text-gray-400">Email address:</p>
                  <p className="text-white font-mono">{emailVerificationError.email}</p>
                </div>
              )}
              
              {resendSuccess && (
                <div className="bg-green-500/20 border border-green-500/50 rounded-xl p-3 mb-4">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-green-400 text-sm">Verification email sent! Please check your inbox.</p>
                  </div>
                </div>
              )}
              
              {resendError && (
                <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-3 mb-4">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-400 text-sm">{resendError}</p>
                  </div>
                </div>
              )}
              
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 mb-4">
                <p className="text-amber-300 text-sm">
                  <strong>Note:</strong> Check your spam folder if you do not see the email in your inbox.
                </p>
              </div>
            </div>
            
            {/* Actions */}
            <div className="px-6 py-4 bg-[#0f0f1a] flex flex-col sm:flex-row gap-3">
              <button
                onClick={closeEmailVerificationError}
                className="flex-1 px-4 py-2 bg-[#252540] text-gray-300 rounded-xl hover:bg-[#2a2a4a] transition-colors"
              >
                Back to Login
              </button>
              {emailVerificationError.canResend && !resendSuccess && (
                <button
                  onClick={handleResendVerificationEmail}
                  disabled={isResendingEmail}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl hover:from-amber-600 hover:to-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isResendingEmail ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Resend Verification Email
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLogin;
